/**
 * HarvestTrophyCard -- displays a harvest record with photo, details, and actions.
 *
 * M81 migration: replaced useHarvestPhoto (which created an object URL on the
 * legacy photo blob) with useHarvestPhotoId (returns a photoId) + PhotoThumbnail
 * (uses usePhotoSrc). M83 (ADR-044) moved IDB byte storage to ArrayBuffer; the
 * Blob is reconstructed inside usePhotoSrc at render time.
 */

import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { Pencil, X } from 'lucide-react';

import type { HarvestRecord } from '@/types/hunt';
import { getPhoto, getPhotosForHarvest } from '@/services/photo-service';
import { PhotoThumbnail } from '@/components/photos/PhotoThumbnail';

interface HarvestTrophyCardProps {
  record: HarvestRecord;
  locationName?: string;
  huntId: string;
  onDelete: (harvestId: string) => void;
}

/**
 * Resolves which photoId to display for this harvest.
 * Returns the thumbnailPhotoId if valid, otherwise the first attached photo's ID.
 * Returns null when no photo is available.
 *
 * M81: returns ID only -- rendering uses PhotoThumbnail / usePhotoSrc, not createObjectURL.
 */
function useHarvestPhotoId(harvestId: string, thumbnailPhotoId?: string): string | null {
  const [photoId, setPhotoId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function resolvePhotoId() {
      if (thumbnailPhotoId) {
        // Try the pinned thumbnail first
        const photo = await getPhoto(thumbnailPhotoId);
        if (!cancelled && photo) {
          setPhotoId(photo.id);
          return;
        }
      }

      // Fall back to first photo attached to this harvest
      const photos = await getPhotosForHarvest(harvestId);
      if (!cancelled && photos[0]) {
        setPhotoId(photos[0].id);
      }
    }

    resolvePhotoId().catch((err: unknown) =>
      console.error('[HarvestTrophyCard] Failed to resolve harvest photo:', err),
    );

    return () => {
      cancelled = true;
    };
  }, [harvestId, thumbnailPhotoId]);

  return photoId;
}

/** Derive a human-readable label for a harvest record. */
function harvestLabel(record: HarvestRecord): string {
  if (record.species === 'Whitetail Deer' || record.species === 'whitetail') {
    if (record.sex === 'buck') {
      if (record.antlerPoints) return `${record.antlerPoints}-Point Buck`;
      return 'Buck';
    }
    if (record.sex === 'doe') return 'Doe';
    return 'Deer';
  }
  return record.species;
}

export function HarvestTrophyCard({
  record,
  locationName,
  huntId,
  onDelete,
}: HarvestTrophyCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  // M81: resolve photo ID only; PhotoThumbnail handles hydration via usePhotoSrc
  const photoId = useHarvestPhotoId(record.id, record.thumbnailPhotoId);

  const label = harvestLabel(record);

  const detailLine = [
    record.weight !== undefined ? `${record.weight} lbs` : null,
    locationName ?? null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className="bg-ember-900/10 border border-ember-800/50 border-l-2 border-l-ember-500 rounded-lg p-4"
      data-testid={`harvest-card-${record.id}`}
      data-variant="trophy"
    >
      <div className="flex items-start gap-3">
        {/* Trophy photo -- rendered via PhotoThumbnail (usePhotoSrc, M81) */}
        {photoId && (
          <div
            className="w-16 h-16 shrink-0 rounded-md overflow-hidden border border-ember-800/40"
            data-testid={`harvest-trophy-photo-${record.id}`}
          >
            <PhotoThumbnail
              photoId={photoId}
              alt="Harvest trophy photo"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="flex flex-col gap-1 min-w-0 flex-1">
          {/* Field-label above title */}
          <p className="field-label">
            HARVEST
          </p>

          {/* Animal description -- editorial weight */}
          <p
            className="font-display text-[20px] leading-snug text-[var(--color-text-primary)]"
            data-testid={`harvest-title-${record.id}`}
          >
            {label}
          </p>

          {detailLine && (
            <p className="text-body-sm text-[var(--color-text-muted)]">{detailLine}</p>
          )}
          <p className="text-body-sm text-[var(--color-text-muted)]">
            {format(parseISO(record.date), 'MMM d, yyyy')}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-1 shrink-0">
          <Link
            to={`/hunt/${huntId}/harvest/${record.id}/edit`}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors p-1"
            aria-label="Edit harvest"
            data-testid={`edit-harvest-btn-${record.id}`}
          >
            <Pencil size={14} />
          </Link>
          {confirmDelete ? (
            <div className="flex gap-2 items-center">
              <button
                type="button"
                onClick={() => onDelete(record.id)}
                className="text-body-xs text-red-400 hover:text-red-300 font-medium"
                data-testid={`confirm-delete-harvest-${record.id}`}
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-body-xs text-[var(--color-text-muted)]"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors p-1"
              aria-label="Delete harvest"
              data-testid={`delete-harvest-btn-${record.id}`}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
